import { FC, useEffect, useState } from "react";
import { Box, Button, Typography, Modal } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExcelJS from "exceljs";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { PayrollRowData, PayrollUserData } from "types/Payroll";
import GetAppIcon from "@material-ui/icons/GetApp";
import { PayrollRenderTable } from "componentsNew/tables/XlsxTable/PayrollRenderTable";
import { downloadPayrollFile, headPayrollRow, sumPayrollRow } from "utils/payrollHelpers";
import { filteredPayrollDataList, filteredPayrollUserDataList } from "utils/filters";
import { closeModal } from "actions/modalActions";
import { useDispatch } from "react-redux";

interface XlsxGenerationModalProps {
  incomingModalName: string;
  applyFilter?: boolean;
}

export const PayrollGenerationModal: FC<XlsxGenerationModalProps> = ({
  incomingModalName,
  applyFilter,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [tableType, setTableType] = useState<"by_total" | "by_da">("by_total");
  const [newDataList, setNewDataList] = useState<PayrollRowData[]>([]);
  const [newUserList, setUserDataList] = useState<PayrollUserData[]>([]);
  const { userData } = useTypeSelectors((state) => state.auth);
  const { modalName } = useTypeSelectors((state) => state.modalState);
  const { dataList, userList, payrollFilters } = useTypeSelectors((state) => state.payrollTable);

  const open = incomingModalName === modalName;

  useEffect(() => {
    if (open) {
      if (applyFilter) {
        setNewDataList(filteredPayrollDataList(dataList, payrollFilters));
        setUserDataList(
          filteredPayrollUserDataList(
            userList,
            payrollFilters,
            filteredPayrollDataList(dataList, payrollFilters),
          ),
        );
      } else {
        setNewDataList(dataList);
        setUserDataList(userList);
      }
    }
  }, [applyFilter, dataList, userList, payrollFilters, open]);

  const onClose = () => dispatch(closeModal());

  const exportTotalToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet1");

    const fileDataList = newDataList.map((report: PayrollRowData) => [
      report?.user_name,
      report?.badge,
      report.station,
      report?.agency,
      report?.date,
      report.role,
      report.role_hours,
      report.pay_rate,
      report?.pay,
      report?.agency_pay,
      report?.comment,
    ]);

    if (fileDataList.length > 0) {
      headPayrollRow(worksheet);

      fileDataList.forEach((row: any) =>
        worksheet.addRow(row).eachCell((cell) => {
          cell.font = { name: "Arial", family: 2, bold: false, size: 10 };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        }),
      );

      sumPayrollRow(worksheet, newDataList, null, true);
    }

    return downloadPayrollFile(applyFilter, workbook, userData.station_spots, payrollFilters);
  };

  const exportInduvidualTotalToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet1");

    if (newUserList.length > 0) {
      headPayrollRow(worksheet);

      newUserList.forEach((user) => {
        const userDataList = newDataList.filter((report) => report.user_id === user.id);
        userDataList
          .map((report: PayrollRowData) => [
            report?.user_name,
            report?.badge,
            report.station,
            report?.agency,
            report?.date,
            report.role,
            report.role_hours,
            report.pay_rate,
            report?.pay,
            report?.agency_pay,
            report?.comment,
          ])
          .forEach((row) =>
            worksheet.addRow(row).eachCell((cell) => {
              cell.font = { name: "Arial", family: 2, bold: false, size: 10 };
              cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
              };
            }),
          );

        sumPayrollRow(worksheet, userDataList, user);

        return newUserList.length - 1 !== newUserList.findIndex((item) => item.id === user.id)
          ? worksheet.addRow(["", "", "", "", "", "", "", "", "", "", ""])
          : null;
      });
    }

    return downloadPayrollFile(applyFilter, workbook, userData.station_spots, payrollFilters);
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box className={classes.modalBox}>
          <Box className={classes.modalTitle}>
            <Typography variant="h3">Table preview</Typography>
            <Button
              onClick={tableType === "by_total" ? exportTotalToExcel : exportInduvidualTotalToExcel}
            >
              <GetAppIcon />
            </Button>
            <Box className={classes.buttonBox}>
              <Button
                variant={tableType === "by_total" ? "contained" : "outlined"}
                className={classes.leftButton}
                onClick={() => setTableType("by_total")}
              >
                By total
              </Button>
              <Button
                variant={tableType === "by_da" ? "contained" : "outlined"}
                className={classes.rightButton}
                onClick={() => setTableType("by_da")}
              >
                By DA
              </Button>
            </Box>
          </Box>
          <PayrollRenderTable type={tableType} dataList={newDataList} userList={newUserList} />
        </Box>
      </Modal>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  modalBox: {
    background: "white",
    display: "flex",
    flexDirection: "column",
    width: "1150px",
    height: "100vh",
    position: "absolute",
    left: "20%",
    overflowY: "scroll",

    "&::-webkit-scrollbar": { width: "8px", height: "8px" },
    "&::-webkit-scrollbar-track": { backgroundColor: theme.palette.background.default },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.light,
      borderRadius: "5px",
    },
  },
  buttonBox: {
    display: "flex",
  },
  leftButton: {
    boxSizing: "border-box",
    borderRadius: "25px 0 0 25px",
    borderRight: "none",
  },
  rightButton: {
    boxSizing: "border-box",
    borderRadius: "0 25px 25px 0",
    borderLeft: "none",
  },
  modalTitle: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    fontWeight: 700,
    justifyContent: "center",
    borderBottom: `1px solid ${theme.palette.primary.light}`,
  },
  button: { textTransform: "none" },
}));
